/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */
import { ModelProperty } from './model-property';
import { DataModelFilters } from './data-model-filters.enum';
import { SortStatus } from '../tool/sort-status.enum';
import { PropertyType } from './property-type.enum';
import { Entity } from '../model/entity/entity';
import { Alias } from '../enum/alias.enum';
import { PropertyGroup } from './property-group';

export class DataModel {
  // Direct property
  public id: string;
  public title: string;
  public type: string;
  public properties: ModelProperty[];
  public required: string[];
  public groups: PropertyGroup[]; // todo inutile ? a supprimer
  public formGroups: PropertyGroup[] = [];

  // Indirect Property
  private _primaryKeyProperty: ModelProperty;
  private _detailViewProperty: ModelProperty;
  private _mainLabelProperties: ModelProperty[];
  public baseEntity: Entity;
  public defaultSort: { property: ModelProperty; sort: SortStatus };
  public defaultGroup: ModelProperty;

  // Constructor
  public constructor(entity?: Entity) {
    this._mainLabelProperties = [];
    this.properties = [];
    this.required = [];
    this.title = '';
    this.baseEntity = entity;
  }

  /**
   * Get the Primary Key properties object or first property if no primary key was detected
   * Return null if properties array of dataModel is empty.
   */
  get primaryKeyProperty(): ModelProperty {
    if (this._primaryKeyProperty) {
      return this._primaryKeyProperty;
    }
    if (this.properties.length > 0) {
      return this.properties.at(0);
    }
    return null;
  }

  /**
   * Get the Primary Key properties object or first property if no primary key was detected
   * Return null if properties array of dataModel is empty.
   */
  get detailViewProperty(): ModelProperty {
    return this._detailViewProperty ? this._detailViewProperty : null;
  }

  /**
   * Get the Main Label properties object or first property if no mainLabel property was detected
   * Return null if properties array of dataModel is empty.
   */
  get mainLabelProperties(): ModelProperty[] {
    if (this._mainLabelProperties.length > 0) {
      return this._mainLabelProperties;
    } else if (this.properties.length > 0) {
      return [this.properties[0]];
    } else {
      return null;
    }
  }

  /**
   * Add a property to the model
   * @param property not null
   */
  addProperty(property: ModelProperty) {
    if (property.primaryKey) {
      this._primaryKeyProperty = property;
    }
    if (property.mainLabel) {
      this._mainLabelProperties.push(property);
    }
    if (property.showOnDetails) {
      this._detailViewProperty = property;
    }
    if (property.defaultSort) {
      this.defaultSort = {
        property: property,
        sort:
          property.defaultSortOrder === 'desc'
            ? SortStatus.down
            : SortStatus.up,
      };
    }
    if (property.defaultGroup) {
      this.defaultGroup = property;
    }
    this.properties.push(property);
  }

  /**
   * Get property of specific context {@see DataModelFilters}
   * @param type of filter
   * @param index (if column form)
   */
  getProperty(type: DataModelFilters, index?: number): ModelProperty[] {
    let properties = [];
    switch (type) {
      case DataModelFilters.table:
        this.properties.forEach((entity) => {
          if (entity.existOnTable) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.showOnTable:
        this.properties.forEach((entity) => {
          if (entity.existOnTable && entity.showOnTable) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.showOnSearch:
        this.properties.forEach((entity) => {
          if (entity.showOnSearch) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.formCreation:
        this.properties.forEach((entity) => {
          if (
            entity.showOnCreate &&
            entity.type !== PropertyType.hide &&
            (index === undefined ||
              entity.column === index ||
              (index === -1 && entity.column === undefined))
          ) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.quickFormCreation:
        this.properties.forEach((entity) => {
          if (
            entity.showOnForm &&
            entity.showOnCreate &&
            entity.showOnQuickForm &&
            entity.type !== PropertyType.hide &&
            (index === undefined || entity.column === index)
          ) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.formUpdate:
        this.properties.forEach((entity) => {
          if (
            entity.showOnUpdate &&
            entity.type !== PropertyType.hide &&
            (index === undefined ||
              entity.column === index ||
              (index === -1 && entity.column === undefined))
          ) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.quickFormUpdate:
        this.properties.forEach((entity) => {
          if (
            entity.showOnForm &&
            entity.showOnUpdate &&
            entity.showOnQuickForm &&
            entity.type !== PropertyType.hide &&
            (index === undefined || entity.column === index)
          ) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.preselection:
        this.properties.forEach((entity) => {
          if (entity.showOnPreselection) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.formHidden:
        this.properties.forEach((entity) => {
          if (entity.editType === PropertyType.hide) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.form:
        this.properties.forEach((entity) => {
          if (entity.editType !== PropertyType.hide) {
            properties.push(entity);
          }
        });
        break;
      case DataModelFilters.details:
        properties = this.properties.filter(
          (property) => property.showOnDetailsView === true,
        );
    }
    return this.sort(properties, type);
  }

  /**
   * Sort properties by position
   * @param properties Data Model Properties
   * @param filter Filter applied on the selection of properties
   */
  private sort(properties: ModelProperty[], filter: DataModelFilters) {
    let sortArray: ModelProperty[] = Array(this.properties.length);
    sortArray = sortArray.fill(null, 0, this.properties.length);
    for (const property of properties) {
      this.changePosition(property, sortArray, filter);
    }
    this.changeLength(sortArray);
    return sortArray;
  }

  /**
   * Check if property has a relation with an other property. If has many relations, return its
   */
  public getRelation(property: ModelProperty): ModelProperty[] {
    return this.properties.filter((p) => {
      if (p.relationObject) {
        for (const field of p.relationObject.fields) {
          /* istanbul ignore else */
          if (property.id === field) {
            return true;
          }
        }
      }
      return false;
    });
  }

  /**
   * Get the columns count
   */
  public getColumnsCount() {
    let count = 0;
    for (const prop of this.properties.filter((p) => p.showOnForm)) {
      /* istanbul ignore else */
      if (prop.column !== undefined && prop.column !== null) {
        /* istanbul ignore else */
        if (prop.column > count) {
          count = prop.column;
        }
      }
    }
    return count;
  }

  /**
   * Find a property by his name
   * @param id
   */
  public find(id: string): ModelProperty {
    return this.properties.find((property) => property.id === id);
  }

  /**
   * Find a property by his alias
   * @param type
   * @param alias
   */
  public findByAlias(type: Alias, alias: string): ModelProperty {
    return this.properties.find((property) => property.isAlias(type, alias));
  }

  /**
   * Return id of primary key field
   */
  public getPrimaryKey(): string {
    return this.primaryKeyProperty ? this.primaryKeyProperty.id : null;
  }

  /**
   * Return array of label keys or empty array if hasn't mainLabelProperties
   */
  public getMainLibKeys(): string[] {
    return this.mainLabelProperties
      ? this.mainLabelProperties.map((prop) => prop.id)
      : [];
  }

  private changePosition(
    property: ModelProperty,
    sortArray: ModelProperty[],
    filter: DataModelFilters,
  ) {
    let position = property.getPositionByContext(filter);
    if (position === undefined) {
      position = property.position;
    }
    if (position !== undefined && position - 1 < this.properties.length) {
      // SI aucun élément a cette position, l'ajoute
      const value = sortArray[position - 1];
      if (value === null) {
        sortArray[position - 1] = property;
      } else {
        //  Si un élément remplace et rejete l'élément à la prochaine case libre
        sortArray.splice(position, 0, property);
      }
    } else {
      for (let i = 0; i < sortArray.length; ++i) {
        if (sortArray[i] === null) {
          sortArray[i] = property;
          break;
        }
      }
    }
  }

  private changeLength(sortArray: ModelProperty[]) {
    let length = sortArray.length;
    for (let i = 0; i < length; ++i) {
      if (!sortArray[i]) {
        sortArray.splice(i, 1);
        --i;
        --length;
      }
    }
  }

  findByField(field: string): ModelProperty[] {
    return this.properties.filter((p) => p.field === field);
  }

  public serialize(): any {
    const serial: any = {};
    serial.id = this.id;
    serial.title = this.title;
    serial.schema = 'http://json-schema.org/schema#';
    serial.type = 'object';
    serial.properties = {};
    this.properties.forEach((p) => {
      serial.properties[p.id] = p.serialize();
    });
    return serial;
  }
}
